import { FGListen, FGNumberInput } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import { DogWorkbookApi, SaveNewWorkbookDto } from "../../../../../api";
import {
  FGSRSHDateMaskInput,
  FGWorkbookTypeSelect,
  SmallFormGenerator,
} from "../../../../../components";
import { useApiService, useSaveApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";

export interface IDogWorkbookDialogPanelProps {
  dogId: number;
  onSaved: () => void;
}

const Warning = styled.div`
  color: "#ff0000";
`;

export const DogWorkbookDialogPanel: React.FunctionComponent<IDogWorkbookDialogPanelProps> = ({
  dogId,
  onSaved,
}) => {
  const { t } = useTl();
  const api = useApiService(DogWorkbookApi);

  const [displayWarning, setDisplayWarning] = React.useState(false);
  React.useEffect(() => {
    api.dogWorkbookAvailable(dogId).then((result) => {
      setDisplayWarning(!result.value);
    });
  }, [api, dogId]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      identifier: Yup.number().required(t(ETLCodes.Required)),
      counter: Yup.number().required(t(ETLCodes.Required)),
      deliveredOn: Yup.date().required(t(ETLCodes.Required)),
      workbookTypeId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "workbookType-available",
          t(ETLCodes.WorkbookRequestOngoingForThisWorkbookType),
          async function (value) {
            if (value) {
              const result = await api.isWorkbookRequestOngoing(value, dogId);
              console.log("result = ", result.value);
              console.log("inverse:", !result.value);
              return !result.value;
            }
            return true;
          }
        ),
    });
  }, [api, dogId, t]);

  const { saving, saveItem } = useSaveApi<SaveNewWorkbookDto>(
    React.useMemo(
      () => ({
        saveApiFn: (data) => api.saveNewWorkbook(data),
        onSaveFn: () => onSaved(),
      }),
      [api, onSaved]
    )
  );
  const initialData = React.useMemo(
    () => new SaveNewWorkbookDto({ dogId: dogId }),
    [dogId]
  );

  return (
    <>
      {displayWarning && (
        <Warning>
          <i className="fas fa-exclamation-circle" />
          {t(ETLCodes.DogWorkbookWarning)}
        </Warning>
      )}
      <SmallFormGenerator
        onSubmit={saveItem}
        initialValues={initialData}
        editMode={true}
        validationSchema={FormSchema}
        minLabelWidth={175}
        saving={saving}
      >
        <FGListen
          field="workbookTypeId"
          onChanged={async (value, formik) => {
            const workbook = await api.getWorkbookInformation(
              value,
              dogId,
              false
            );
            formik.setFieldValue("identifier", workbook.identifier);
            formik.setFieldValue("counter", workbook.counter);
          }}
        />
        <FGWorkbookTypeSelect
          name="workbookTypeId"
          label={t(ETLCodes.WorkbookType)}
          dogId={dogId}
        />
        <FGNumberInput name="identifier" label={t(ETLCodes.Number)} readonly />
        <FGNumberInput label={t(ETLCodes.Counter)} name="counter" min={0} />
        <FGSRSHDateMaskInput
          name="deliveredOn"
          label={t(ETLCodes.DeliveredOn)}
        />
      </SmallFormGenerator>
    </>
  );
};
